import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";

import { detectionSourceLabel, isAutoDetected } from "../../DTOs/Common/DetectionSource";
import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { geologyAjax } from "../../ajax/GeologyAjax";
import { ButtonCrumb } from "../../components/Breadcrumbs/ButtonCrumb";
import { DateSelectCrumb } from "../../components/Breadcrumbs/DateSelectCrumb";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { useDataTable, type DataTableFilteredRequest } from "../../components/DataTable/TableHook";
import { useAutoPageSize } from "../../components/Hooks/useAutoPageSize";
import { setCurrentView } from "../../state/reducers/viewReducer";
import { RootState } from "../../state/store";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { useOptions } from "../../components/Hooks/useOptions";
import { useOnlineStatus } from "../../components/Hooks/useOnlineStatus";
import { useRehandleCollectsTruckBucketType } from "../../components/Hooks/useRehandleConfig";
import { RehandleGroupsTable } from "./RehandleGroupsTable";
import { sumCachedLoads } from "../../offline/patchCachedDiggingSummary";
import { InsertGeoRehandleRow } from "./InsertGeologyRehandleRow";
import { DeleteButton } from "../../components/Buttons/DeleteButton";
import Button from "../../components/Buttons/Button";
import SaveButton from "../../components/Buttons/SaveButton";
import { VerificationIndicator } from "../../components/VerificationIndicator/VerificationIndicator";
import { GeoRehandleDiggerDTO } from "../../DTOs/Geology/GeoRehandleDiggerDTO";
import { GeoRehandleTruckingTruckDTO } from "../../DTOs/Geology/GeoRehandleTruckingTruckDTO";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { TripleShift } from "../../types/Shift";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import DiggerCell from "./TableCellsComponents/DiggerCell";
import PlantModelCell from "./TableCellsComponents/PlantModelCell";
import MovementFromCell from "./TableCellsComponents/MovementFromCell";
import MovementToCell from "./TableCellsComponents/MovementToCell";
import { ShiftSelectCrumb } from "../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { GetGeoRehandlesPagedResultDTO } from "../../DTOs/Geology/GetGeoRehandlesPagedResultDTO";
import GeologyRehandleTableFooter from "./GeologyRehandleTableFooter";
import GeologyRehandleTableNoResultsContent from "./GeologyRehandleNoResultsContent";
import { TablePage } from "../../components/Layout/TablePage";
import { useSignalRContext } from "../../components/Contexts/SignalRContext";
import { geoRehandleMutations } from "../../offline/geoRehandleMutations";

type GeologyRehandleTableMetaData = {
  totalLoads: number;
  totalTonnes: number;
}

const GeologyRehandle: React.FC = () => {
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const isShiftLocked = useSelector((state: RootState) => state.shift.isLocked);
  const canToggleLock = useSelector((s: RootState) => s.shift.canToggleLock);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { connection } = useSignalRContext();
  const queryClient = useQueryClient();
  const { selectedDate, selectedShift } = useShiftTimes();
  const isOnline = useOnlineStatus();
  const [localSelectedShift, _setLocalSelectedShift] = useState<TripleShift>(selectedShift)

  const setLocalSelectedShift = (newShift: TripleShift) => {
    _setLocalSelectedShift(newShift);

    if (newShift === "DAY" || newShift === "NIGHT") {
      dispatch(setSelectedShift(newShift));
    }
  };

  const [insertRowState, setInsertRowState] = useState<GeoRehandleDTO>(new GeoRehandleDTO({
    geoRehandleId: 0,
    operationId: 0,
    bucketTypeId: 0,
    movementFromId: 0,
    movementToId: 0
  }));

  const {
    options: diggerOptions,
    isLoading: isDiggerOptionsLoading
  } = useOptions<GeoRehandleDiggerDTO, GeoRehandleDiggerDTO>(
    ['geology', 'rehandleDiggers', selectedDate.toISODate(), localSelectedShift],
    () => geologyAjax.getRehandleDiggers(selectedDate, localSelectedShift),
    x => x,
  );

  // Note: with this off a rehandle is one machine carrying its own load, so the row's bucket type is that
  // machine's and the backend derives it. The Plant Model cell is dropped rather than disabled, and the two
  // movement columns take the freed width, which lands the screen on the same 20/30/10/30/10/10 layout the
  // Crushing screen already uses for exactly this shape of row.
  const collectsTruckBucketType = useRehandleCollectsTruckBucketType();

  // Note: not persisted. Grouping is how somebody wants to look at THIS shift right now - a shift of
  // telemetry wants collapsing, a shift of hand-entered rows does not - so remembering the choice across
  // dates would as often be wrong as right.
  const [isGrouped, setIsGrouped] = useState(false);
  const movementColumnBasis = collectsTruckBucketType ? "basis-[20%]" : "basis-[30%]";

  const {
    options: rehandleTrucks,
    isLoading: bucketTypeOptionsLoading
  } = useOptions<GeoRehandleTruckingTruckDTO, GeoRehandleTruckingTruckDTO>(
    ['geology', 'rehandleTrucks', selectedDate.toISODate()],
    () => geologyAjax.getRehandleTruckingTrucks(selectedDate),
    x => x,
  );

  const bucketTypeOptions = useMemo(
    () => rehandleTrucks.filter((truck, index) =>
      rehandleTrucks.findIndex(other => other.bucketTypeId === truck.bucketTypeId) === index),
    [rehandleTrucks],
  );

  const {
    options: rehandleMovementFromLocationOptions,
    isLoading: isRehandleMovementFromLocationOptionsLoading
  } = useOptions<LocationDTO, { value: number, label: string, isDumpOrCrusher: boolean }>(
    ['geology', 'rehandleFromLocations', selectedDate.toISODate()],
    () => geologyAjax.getRehandleMovementFromLocations(selectedDate),
    x => ({
      value: x.id ?? 0,
      label: x.name,
      isDumpOrCrusher: x.locationTypeId === 3 || x.locationTypeId === 6
    }),
  );

  const {
    options: rehandleMovementToLocationOptions,
    isLoading: isRehandleMovementToLocationOptionsLoading
  } = useOptions<LocationDTO, { value: number, label: string, isDumpOrCrusher: boolean }>(
    ['geology', 'rehandleToLocations', selectedDate.toISODate()],
    () => geologyAjax.getRehandleMovementToLocations(selectedDate),
    x => ({
      value: x.id ?? 0,
      label: x.name,
      isDumpOrCrusher: x.locationTypeId === 3 || x.locationTypeId === 6
    }),
  );

  const handleUpdateRow = useCallback(async (row: GeoRehandleDTO) => {
    let isValid = true;

    if (!row.operationId) {
      isValid = false;
      enqueueSnackbar('Please select a Digger', { variant: 'error' });
    }
    if (!row.bucketTypeId) {
      isValid = false;
      enqueueSnackbar('Please select a bucket type', { variant: 'error' });
    }
    if (!row.movementFromId) {
      isValid = false;
      enqueueSnackbar('Please select a location to move from', { variant: 'error' });
    }
    if (!row.loads) {
      isValid = false;
      enqueueSnackbar('Please enter the number of loads to move', { variant: 'error' });
    }
    if (!row.movementToId) {
      isValid = false;
      enqueueSnackbar('Please select a location to move to', { variant: 'error' });
    }
    if (row.movementFromId === row.movementToId) {
      isValid = false;
      enqueueSnackbar('Please select a different location to move to', { variant: 'error' });
    }

    if (!isValid || row.geoRehandleId === 0) return;

    const response = await geoRehandleMutations.updateGeoRehandle(row)

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      invalidate();
      return false;
    }

    enqueueSnackbar('Rehandle Entry updated successfully', { variant: 'success' });
    const { rehandle, totalLoads, totalTonnes } = response.data!;
    updateRowSource((r) => r.geoRehandleId === rehandle!.geoRehandleId, rehandle!, { totalLoads, totalTonnes });

    return true;
  }, [enqueueSnackbar]);

  const handleAddRow = useCallback(async () => {
    let isValid = true;

    if (!insertRowState.operationId) {
      enqueueSnackbar('Please select a Digger', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.bucketTypeId) {
      enqueueSnackbar('Please select a Plant Model', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.movementFromId) {
      enqueueSnackbar('Please select a location to move from', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.loads) {
      enqueueSnackbar('Please enter the number of loads to move', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.movementToId) {
      enqueueSnackbar('Please select a location to move to', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!isValid) return false;

    const response = await geoRehandleMutations.addGeoRehandle(insertRowState, selectedDate, localSelectedShift);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 * (index + 1) });
      });
      return false;
    }

    enqueueSnackbar('Rehandle Entry added successfully', { variant: 'success' });

    const { rehandle, totalLoads, totalTonnes } = response.data!;
    appendRowSource(rehandle!, { totalLoads, totalTonnes });
    setInsertRowState(new GeoRehandleDTO({
      geoRehandleId: 0,
      operationId: 0,
      bucketTypeId: 0,
      movementFromId: 0,
      movementToId: 0
    }));
    return true;
  }, [insertRowState, localSelectedShift, selectedDate, enqueueSnackbar]);

  const deleteRow = useCallback(async (rowId: number) => {
    const response = await geoRehandleMutations.deleteGeoRehandle(rowId);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    const { totalLoads, totalTonnes } = response.data!;
    removeRowSource((r) => r.geoRehandleId === rowId, { totalLoads, totalTonnes });
    enqueueSnackbar('Ore Rehandle entry deleted successfully', { variant: 'success', autoHideDuration: 3000 })

    return true;
  }, [enqueueSnackbar]);

  const getRows = useCallback((req: DataTableFilteredRequest) => geologyAjax.getGeoRehandles(selectedDate, localSelectedShift, req), [selectedDate, localSelectedShift]);

  // Note: held in a ref because useDataTable is declared BELOW - it takes translateRows, which needs
  // verifyRow, which needs the updater.
  const updateRowSourceRef = useRef<((predicate: (r: GeoRehandleDTO) => boolean, row: GeoRehandleDTO) => void) | null>(null);

  // Note: accepts one inferred row without touching its values. The row is patched in place rather than
  // refetched, so the badge clears immediately; a failure leaves it exactly as it was and says why.
  const verifyRow = useCallback(async (entry: GeoRehandleDTO) => {
    const response = await geologyAjax.verifyGeoRehandle(entry.geoRehandleId!);

    if (!response.success) {
      response.errors?.forEach(error =>
        enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 6000 }));
      return;
    }

    updateRowSourceRef.current?.(
      (r: GeoRehandleDTO) => r.geoRehandleId === entry.geoRehandleId,
      { ...entry, isVerified: true } as GeoRehandleDTO);

    enqueueSnackbar("Row accepted", { variant: "success", autoHideDuration: 2500 });
  }, [enqueueSnackbar]);

  const translateRows = useCallback((entry: GeoRehandleDTO): TableRow => {
    return {
      id: entry.geoRehandleId ?? 0,
      values: [
        {
          name: "Digger",
          editable: false,
          type: "custom",
          value: entry.operationId,
          tailwindClasses: "basis-[20%] py-1 pl-1",
          render: () => (
            <DiggerCell
              entry={entry}
              diggerOptions={diggerOptions}
              isDiggerOptionsLoading={isDiggerOptionsLoading}
              handleUpdateRow={handleUpdateRow}
              isShiftLocked={isShiftLocked}
            />
          )
        },
        ...(collectsTruckBucketType ? [{
          name: "Plant Model",
          editable: false,
          type: "custom" as const,
          value: entry.bucketTypeId,
          tailwindClasses: "basis-[20%] py-1",
          render: () => (
            <PlantModelCell
              entry={entry}
              bucketTypeOptions={bucketTypeOptions}
              bucketTypeOptionsLoading={bucketTypeOptionsLoading}
              handleUpdateRow={handleUpdateRow}
              isShiftLocked={isShiftLocked}
            />
          )
        }] : []),
        {
          name: "Movement From",
          editable: false,
          type: "custom",
          value: entry.movementFromId,
          tailwindClasses: movementColumnBasis,
          render:
            () => (
              <MovementFromCell
                entry={entry}
                locationOptions={rehandleMovementFromLocationOptions}
                locationOptionsLoading={isRehandleMovementFromLocationOptionsLoading}
                handleUpdateRow={handleUpdateRow}
                isShiftLocked={isShiftLocked}
              />
            )
        },
        {
          name: "Loads",
          editable: true,
          type: "integer",
          value: entry.loads,
          tailwindClasses: "basis-[10%] py-1",
          disabled: isShiftLocked,
          onChangeDebounced: async (_, newValue) => await handleUpdateRow({ ...entry, loads: newValue }),
          onChangeDebouncedMs: 1200,
        },
        {
          name: "Movement To",
          editable: false,
          type: "custom",
          value: entry.movementToId,
          tailwindClasses: movementColumnBasis,
          render:
            () => (
              <MovementToCell
                entry={entry}
                locationOptions={rehandleMovementToLocationOptions}
                locationOptionsLoading={isRehandleMovementToLocationOptionsLoading}
                handleUpdateRow={handleUpdateRow}
                isShiftLocked={isShiftLocked}
              />
            )
        },
        {
          // Note: the same shape Stoppages.tsx and TrucksTable.tsx use for provenance, so an inferred
          // rehandle reads the way inferred data reads everywhere else in Eclipse rather than announcing
          // itself a third way. Both this screen and Crushing show it: they are the same tblGeoRehandle
          // rows split on whether the destination is a crusher, so marking only one would leave inferred
          // stockpile-to-crusher rows looking hand-entered.
          name: 'Source',
          type: 'custom',
          value: entry.isVerified === false ? 'Needs checking' : '',
          tailwindClasses: 'w-full basis-[10%]',
          render: () => (
            <div className="flex items-center justify-center gap-1.5 w-full">
              <div className="text-theme-text-muted text-xs">
                {detectionSourceLabel(entry.detectionSource)}
              </div>
              {/* Note: the mark IS the accept control. Clicking it accepts the row as it stands, which is
                  the only way a row the FMS got right ever leaves the queue - editing clears the flag too,
                  but only as a side effect of changing something. */}
              {isAutoDetected(entry.detectionSource) && (
                <VerificationIndicator
                  isVerified={entry.isVerified !== false}
                  onAccept={() => verifyRow(entry)}
                  subject="rehandle row"
                  disabled={isShiftLocked}
                />
              )}
            </div>
          )
        },
        {
          name: 'Actions',
          type: 'custom',
          value: 'custom',
          tailwindClasses: 'basis-[10%] pr-1',
          render: (_, tableRow) =>
            <div className="flex justify-center flex-wrap gap-1">
              <DeleteButton
                handleDelete={() => deleteRow(tableRow.id)}
                disabled={isShiftLocked}
              />
            </div>
        }
      ]
    }
  }, [
    bucketTypeOptions,
    bucketTypeOptionsLoading,
    rehandleMovementToLocationOptions,
    rehandleMovementFromLocationOptions,
    isRehandleMovementToLocationOptionsLoading,
    isRehandleMovementFromLocationOptionsLoading,
    diggerOptions,
    isDiggerOptionsLoading,
    handleUpdateRow,
    deleteRow,
    isShiftLocked
  ]);

  const getGeologyRehandleTableMetaData = useCallback((response: GetGeoRehandlesPagedResultDTO) => ({
    totalLoads: response.totalLoads,
    totalTonnes: response.totalTonnes,
  }), []);

  const { pageSize: fitPageSize, onAutoPageSize } = useAutoPageSize();

  const {
    displayData,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    pageSize,
    totalPages,
    totalCount,
    sortField,
    sortDirection,
    setPage,
    appendRowSource,
    removeRowSource,
    updateRowSource,
    invalidate,
    tableMetaData
  } = useDataTable<GeoRehandleDTO, number, GeologyRehandleTableMetaData, GetGeoRehandlesPagedResultDTO>({
    queryKey: ['geology', 'rehandles', selectedDate.toISODate(), localSelectedShift],
    fetchFn: getRows,
    translateFn: translateRows,
    initialPage: 1,
    pageSize: fitPageSize,
    clientPaginate: true,
    selectMeta: getGeologyRehandleTableMetaData
  });

  updateRowSourceRef.current = updateRowSource;

  // Note: one button for a shift's worth, because telemetry writes a row per haul and accepting them one at
  // a time does not scale - the same reason the trucks table has Verify All. Declared AFTER useDataTable
  // because it needs invalidate; the server reports only how many changed, so the table is refetched rather
  // than each row patched.
  const machineLabel = useCallback(
    (operationId: number) => diggerOptions.find(d => d.operationId === operationId)?.plantRef ?? "-",
    [diggerOptions]);

  const locationLabel = useCallback((locationId?: number) => {
    if (locationId == null) return "-";
    return rehandleMovementFromLocationOptions.find(l => l.value === locationId)?.label
      ?? rehandleMovementToLocationOptions.find(l => l.value === locationId)?.label
      ?? "-";
  }, [rehandleMovementFromLocationOptions, rehandleMovementToLocationOptions]);

  const handleAcceptAll = useCallback(async () => {
    const response = await geologyAjax.verifyAllGeoRehandles(selectedDate, localSelectedShift, false);

    if (!response.success) {
      response.errors?.forEach((error, i) =>
        enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 4000 + i * 1000 }));
      return false;
    }

    const accepted = response.data ?? 0;
    enqueueSnackbar(
      accepted === 0 ? "Nothing needed checking on this shift"
        : `${accepted} row${accepted === 1 ? "" : "s"} accepted`,
      { variant: "success", autoHideDuration: 3000 });

    invalidate();
    return true;
  }, [selectedDate, localSelectedShift, enqueueSnackbar, invalidate]);

  useEffect(() => {
    const newView = "GeologyRehandle";

    if (currentView !== newView) {
      dispatch(setCurrentView(newView));
    }
  }, [currentView, dispatch]);

  const handleGeoMovementsChanged = useCallback(() => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: ['geology', 'rehandleFromLocations'] });
    queryClient.invalidateQueries({ queryKey: ['geology', 'rehandleToLocations'] });
  }, [invalidate, queryClient]);

  useEffect(() => {
    if (!connection) return;
    connection.on("DataUpdatedAsync", handleGeoMovementsChanged);
    return () => {
      connection.off("DataUpdatedAsync", handleGeoMovementsChanged);
    };
  }, [connection, handleGeoMovementsChanged]);

  return (
    <TablePage>
      <Breadcrumbs>
        <DateSelectCrumb
          selectedDate={selectedDate}
          setSelectedDate={date => dispatch(setSelectedDate(date.toISODate()!))}
        />
        <ShiftSelectCrumb
          showAfternoonShift
          selectedShift={localSelectedShift}
          setSelectedShift={setLocalSelectedShift}
        />
        <ButtonCrumb buttonText={"Rehandle"} />
      </Breadcrumbs>
      <div className="flex justify-end w-full mb-2">
        <Button
          variant={isGrouped ? "primary" : "secondary"}
          size="sm"
          onClick={() => setIsGrouped(g => !g)}
        >
          {isGrouped ? "Showing groups" : "Group movements"}
        </Button>
      </div>
      {isGrouped ? (
        <RehandleGroupsTable
          selectedDate={selectedDate}
          selectedShift={localSelectedShift}
          isCrushing={false}
          machineLabel={machineLabel}
          locationLabel={locationLabel}
          isShiftLocked={isShiftLocked}
          isOnline={isOnline}
        />
      ) : (
      <DataTable
        data={displayData}
        isLoading={isLoading}
        isBackgroundFetching={isBackgroundFetching}
        error={error}
        pageNumber={pageNumber}
        totalCount={totalCount}
        pageSize={pageSize}
        totalPages={totalPages}
        setPage={setPage}
        sortField={sortField}
        sortDirection={sortDirection}
        minTableWidthPixels={1080}
        fillHeight
        autoPageSize
        onAutoPageSize={onAutoPageSize}
        estimatedRowHeightPx={60}
        noResultsContent={<GeologyRehandleTableNoResultsContent
          isShiftLocked={isShiftLocked}
          canToggleLock={canToggleLock}
        />}
        tableHeaderContent={
          <div className="flex flex-col w-full items-center fade-up">
            <div className="container overflow-y-hidden">
              <div className="bg-theme-surface-2 px-2 py-2 border border-theme-table-border mb-4 short:mb-2 shadow text-theme-text">
                <div className="flex items-center justify-end gap-4">
                  <span className="opacity-70 text-sm">
                    Rows the telemetry recorded arrive marked &quot;Needs checking&quot;.
                  </span>
                  <SaveButton
                    buttonLabel={"Accept All"}
                    confirmMessage={"Mark every unchecked rehandle row on this shift as checked?"}
                    disabled={isShiftLocked || !isOnline}
                    handleSave={handleAcceptAll}
                  />
                </div>
              </div>
            </div>
          </div>
        }
        tableFooterContent={<GeologyRehandleTableFooter
          isLoading={isLoading}
          totalLoads={isOnline
            ? tableMetaData?.totalLoads
            : sumCachedLoads(['dataTable', 'geology', 'rehandles', selectedDate.toISODate(), localSelectedShift, 'all', false, null])}
        />}
        insertRowRenderer={() => <InsertGeoRehandleRow
          state={insertRowState}
          setState={setInsertRowState}
          diggerOptions={diggerOptions}
          isDiggerOptionsLoading={isDiggerOptionsLoading}
          bucketTypeOptions={bucketTypeOptions}
          bucketTypeOptionsLoading={bucketTypeOptionsLoading}
          collectsTruckBucketType={collectsTruckBucketType}
          rehandleMovementFromLocationOptions={rehandleMovementFromLocationOptions}
          isRehandleMovementFromLocationOptionsLoading={isRehandleMovementFromLocationOptionsLoading}
          rehandleMovementToLocationOptions={rehandleMovementToLocationOptions}
          isRehandleMovementToLocationOptionsLoading={isRehandleMovementToLocationOptionsLoading}
          onAdd={handleAddRow}
          isShiftLocked={isShiftLocked}
        />}
        overrideHeaderCells={[
          { name: "Digger", tailwindClasses: "basis-[20%] py-1 pl-1" },
          ...(collectsTruckBucketType
            ? [{ name: "Plant Model", tailwindClasses: "basis-[20%] py-1" }]
            : []),
          { name: "Movement From", tailwindClasses: movementColumnBasis },
          { name: "Loads", tailwindClasses: "basis-[10%] py-1" },
          { name: "Movement To", tailwindClasses: movementColumnBasis },
          { name: "Source", tailwindClasses: "basis-[10%]" },
          { name: "Actions", tailwindClasses: "basis-[10%] pr-1" }
        ]}
      />
      )}
    </TablePage>
  )
}

export default GeologyRehandle